import React, { Fragment, useEffect, useState } from "react";
import { Transition } from "@headlessui/react";
import { CheckCircleIcon, ExclamationCircleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import classnames from "classnames";

export interface ToastProps {
  data: {
    type?: 'success' | 'error'
    message?: string
    description?: string
  }
  onDismiss?: () => void
}

export const Toast: React.FC<ToastProps> = (props) => {
  const { type, message, description } = props.data
  const isError = type === 'error'
  const [show, setShow] = useState<boolean>(true)

  useEffect(() => {
    if (isError) return
    const timeout = setTimeout(() => {
      setShow(false)
    }, 7000)

    return () => {
      clearTimeout(timeout)
    }
  }, [isError])

  const handleDismiss = () => {
    setShow(false)
    props.onDismiss?.()
  }

  return (
    <Transition
      show={show}
      as={Fragment}
      enter="transform ease-out duration-300 transition"
      enterFrom="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
      enterTo="translate-y-0 opacity-100 sm:translate-x-0"
      leave="transition ease-in duration-100"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
    >
      <div
        className={classnames(
          'z-[1000] max-w-sm w-full shadow-lg rounded-lg pointer-events-auto ring-1 ring-black dark:ring-white ring-opacity-5 dark:ring-opacity-5 overflow-hidden',
          {
            'bg-emerald-500 dark:bg-emerald-500': type === 'success' || type == null,
            'bg-red-500 dark:bg-red-500': isError,
          }
        )}
      >
        <div className="p-4">
          <div className="flex items-start">
            <div className="shrink-0">
              {isError
                ? <ExclamationCircleIcon className="h-6 w-6 text-white" aria-hidden="true" />
                : <CheckCircleIcon className="h-6 w-6 text-white" aria-hidden="true" />
              }
            </div>
            <div className="ml-3 w-0 flex-1 pt-0.5">
              <p className="text-sm font-semibold text-white dark:text-white">{message}</p>
              {description !== undefined && <p className="mt-1 text-xs font-normal text-white/90">{description}</p>}
            </div>
            {isError && (
              <div className="ml-4 shrink-0 flex">
                <button
                  type="button"
                  className="inline-flex rounded-md text-white/70 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/50"
                  onClick={handleDismiss}
                >
                  <span className="sr-only">Close</span>
                  <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </Transition>
  )
}

export default Toast;
